import JSZip from 'jszip'
import { generateSalesMaterialUtil, MediaType } from '../../../utils/mediaGenerator'
import { PosterType, Booklet } from '../../../../../types/generated.types'
import { copyZipFilesToMaster } from './zipHelpers'
import { BookletFolder, sanitizeFileSystemName } from '../../../utils/bookletFolderUtils'

interface GenerateResellerParams {
  associationBooklets: Booklet[]
  associationId: string
  generateSalesMaterialMutation: any
  masterZip: JSZip
  bookletFolders: BookletFolder[]
}

const findBookletFolderName = (bookletFolders: BookletFolder[], title: string): string => {
  const match = bookletFolders.find((folder) => folder.title === title)
  return match ? match.folderName : title
}

/**
 * Generates all reseller materials (föreningsaffisch, lagaffisch, klassaffisch, studentaffisch, bilder med olika budskap)
 * and adds them directly to the master ZIP
 */
export const generateResellerMaterials = async ({
  associationBooklets,
  associationId,
  generateSalesMaterialMutation,
  masterZip,
  bookletFolders
}: GenerateResellerParams): Promise<void> => {
  // 6.1 Föreningsaffisch "stärk ekonomin" (index 2)
  try {
    const associationPosterPromises = associationBooklets.map(async (booklet) => {
      const response = await generateSalesMaterialMutation({
        variables: {
          bookletId: booklet._id,
          associationId: associationId,
          type: 'associationPoster'
        }
      })
      const files = response.data?.generateSalesMaterial || []
      if (files.length >= 3) {
        return { booklet, pdf: files[2] }
      }
      return null
    })
    const associationPosterResults = await Promise.all(associationPosterPromises)

    await Promise.all(
      associationPosterResults
        .filter((result) => result !== null)
        .map(async (result) => {
          if (!result) return
          const byteString = atob(result.pdf)
          const ab = new ArrayBuffer(byteString.length)
          const ia = new Uint8Array(ab)
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i)
          }
          const blob = new Blob([ab], { type: 'application/pdf' })
          const fileName = `${sanitizeFileSystemName(result.booklet.title)}-affisch.pdf`
          const folderName = findBookletFolderName(bookletFolders, result.booklet.title)
          masterZip.file(
            `${folderName}/Återförsäljare/Föreningsaffisch/${fileName}`,
            blob
          )
        })
    )
  } catch (error) {
    console.error('Error generating association poster:', error)
  }

  // 6.2 Lagaffisch (schoolClassPoster med PosterType.Team)
  try {
    const { zip: teamPosterZip } = await generateSalesMaterialUtil(
      associationBooklets,
      associationId,
      MediaType.SCHOOL_CLASS_POSTER,
      generateSalesMaterialMutation,
      PosterType.Team
    )
    await copyZipFilesToMaster(teamPosterZip, masterZip, 'Återförsäljare/Lagaffisch/', {
      bookletFolders
    })
  } catch (error) {
    console.error('Error generating team poster:', error)
  }

  // 6.3 Klassaffisch (schoolClassPoster med PosterType.ClassTrip)
  try {
    const { zip: classTripPosterZip } = await generateSalesMaterialUtil(
      associationBooklets,
      associationId,
      MediaType.SCHOOL_CLASS_POSTER,
      generateSalesMaterialMutation,
      PosterType.ClassTrip
    )
    await copyZipFilesToMaster(classTripPosterZip, masterZip, 'Återförsäljare/Klassaffisch/', {
      bookletFolders
    })
  } catch (error) {
    console.error('Error generating class trip poster:', error)
  }

  // 6.4 Studentaffisch (schoolClassPoster med PosterType.Graduation)
  try {
    const { zip: graduationPosterZip } = await generateSalesMaterialUtil(
      associationBooklets,
      associationId,
      MediaType.SCHOOL_CLASS_POSTER,
      generateSalesMaterialMutation,
      PosterType.Graduation
    )
    await copyZipFilesToMaster(graduationPosterZip, masterZip, 'Återförsäljare/Studentaffisch/', {
      bookletFolders
    })
  } catch (error) {
    console.error('Error generating graduation poster:', error)
  }

  // 6.5 Bilder med olika budskap (schoolClassSocialMedia med alla 4 PosterType-variationer)
  try {
    const resellerSocialMediaZip = new JSZip()
    const posterTypes = [
      { type: PosterType.ClassTrip, folderName: 'klassresa' },
      { type: PosterType.Graduation, folderName: 'studenten' },
      { type: PosterType.Team, folderName: 'laget' },
      { type: PosterType.Association, folderName: 'foreningen' }
    ]

    const socialMediaPromises = posterTypes.map(async ({ type, folderName }) => {
      const { zip: socialMediaZip } = await generateSalesMaterialUtil(
        associationBooklets,
        associationId,
        MediaType.SCHOOL_CLASS_SOCIAL_MEDIA,
        generateSalesMaterialMutation,
        type
      )

      await Promise.all(
        Object.keys(socialMediaZip.files).map(async (filename) => {
          const file = socialMediaZip.files[filename]
          if (!file || file.dir) {
            return
          }
          const fileBlob = await file.async('blob')
          resellerSocialMediaZip.file(`${folderName}/${filename}`, fileBlob)
        })
      )
    })

    await Promise.all(socialMediaPromises)

    await copyZipFilesToMaster(resellerSocialMediaZip, masterZip, 'Återförsäljare/Bilder med olika budskap/', {
      bookletFolders
    })
  } catch (error) {
    console.error('Error generating reseller social media images:', error)
  }
}

